import type { AuditFindingPartial, AuditRule } from "../types";
import { addressFromRC } from "../addressing";
import { ValueKind } from "../../../model";

/**
 * Flag any cell whose value-kind is ERROR, regardless of origin.
 * Catches both formula cells evaluating to errors (#N/A, #REF!, etc.)
 * and constant cells containing error literals.
 *
 * Complements `hardcodedErrorLiteral`, which fires only on constant
 * error cells. This rule fires more broadly; the two together give
 * full coverage with appropriate severities (error vs warning).
 *
 * The substrate doesn't intern the specific error literal text (it's
 * lossy by design — ERROR is a value-kind, not an interning bucket),
 * so the message is generic. The cell address tells the user which
 * cell to inspect for the specific error.
 */
export const brokenReferences: AuditRule = {
  id: "system.audit.broken-references",
  name: "Broken references",
  description:
    "Cells whose value is an Excel error (#REF!, #N/A, #DIV/0!, #VALUE!, #NAME?, #NULL!, #NUM!).",
  enabled: true,
  source: "system",
  severity: "error",
  check(sheet) {
    const findings: AuditFindingPartial[] = [];
    const { rows, cols, startRow, startCol } = sheet.bounds;
    const cellValueKind = sheet.cellValueKind;

    for (let r = 0; r < rows; r++) {
      const rowBase = r * cols;
      for (let c = 0; c < cols; c++) {
        const idx = rowBase + c;
        if (cellValueKind[idx] !== ValueKind.ERROR) continue;
        findings.push({
          sheetName: sheet.name,
          address: addressFromRC(startRow + r, startCol + c),
          message: "error cell",
        });
      }
    }
    return findings;
  },
};
